import React, {useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {OpenInNew} from "@mui/icons-material";
import {useHistory} from "react-router-dom";
import {DataGridActionTypes, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState,} from "../../../../../../../packages/data-grid";
import {CustomerNotesDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import IconButton from "../../../../../../components/base/button/icon_button";
import useNotebookTagsInfo from "../../../../../../hooks/use-note-book-tags-info";
import {useOperatorInformation} from "../../../../../../hooks/use-operator-information";


const dataGridSaveConfig = {
    name: 'CustomerNotesDataGrid',
    version: 1,
};

export const CustomerNotesListActionNames = {
    Edit: 'edit',
    Delete: 'delete',
};

const SourceCell = ({noteTags}) => {
    const {pageInfo} = useNotebookTagsInfo(noteTags);

    if (!pageInfo?.title) {
        return null;
    }

    return pageInfo.title;
};

const NavigateCell = ({noteTags}) => {
    const {pageInfo} = useNotebookTagsInfo(noteTags);
    const history = useHistory();

    if (!pageInfo) {
        return null;
    }

    const openSourcePage = () => {
        if (pageInfo.url) {
            history.push(pageInfo.url);
            return;
        }
        pageInfo.action?.();
    };

    return (
        <IconButton onPressed={openSourcePage} color={'paper.secondary'}>
            <OpenInNew className='icon normal'/>
        </IconButton>
    );
};

const CustomerNotesListView = ({
                                   notes,
                                   loading,
                                   updateNote,
                                   removeNote,
                                   getExportInfo,
                               }) => {
    /** @type {React.MutableRefObject<DataGridController>} */
    const dataGridControllerRef = useRef(null);
    const operatorInformation = useOperatorInformation();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customer-notes.list'});

    const notesListColumns = useMemo(() =>
        /** @type {DataGridColumn[]} */
        [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 100,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: true,
                pinned: true,
            },
            {
                title: t('columns.operator'),
                name: CustomerNotesDataGridCellKeys.submittedByName,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 140,
            },
            {
                title: t('columns.modifiedDate'),
                name: CustomerNotesDataGridCellKeys.submitDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 140,
            },
            {
                title: t('columns.source'),
                name: CustomerNotesDataGridCellKeys.source,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 180,
            },
            {
                name: CustomerNotesDataGridCellKeys.navigate,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                pinned: true,
                width: 0,
            },
            {
                title: t('columns.visibility'),
                name: CustomerNotesDataGridCellKeys.visibility,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 100,
            },
            {
                title: t('columns.title'),
                name: CustomerNotesDataGridCellKeys.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 200,
            },
            {
                title: t('columns.note'),
                name: CustomerNotesDataGridCellKeys.content,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: false,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 280,
            },
        ], [t]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: notesListColumns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]} */
        [
            {
                name: CustomerNotesListActionNames.Edit,
                title: t('actions.edit'),
                visibilityConstraints: {
                    minSelectedRows: 1,
                    maxSelectedRows: 1,
                },
            },
            {
                name: CustomerNotesListActionNames.Delete,
                title: t('actions.delete'),
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [t]);

    /**
     * Opens remove confirmation for selected notes.
     */
    const getRemoveConfirmation = (noteIds) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {t('removeConfirmation', {count: noteIds.length})}
                </p>
            </div>,
            {maxWidth: 'sm'},
            async (confirmed) => {
                if (!confirmed) {
                    return;
                }
                await Promise.all(noteIds.map((noteId) => removeNote(noteId)));
            },
        );
    };

    /**
     * Handles data grid footer actions.
     */
    const onActionInvoked = async (action, selectedRows, _excludedRows, allRowsSelected) => {
        dataGridControllerRef.current?.resetSelection();
        const selectedNoteIds = allRowsSelected
            ? notes.map((note) => note.id)
            : selectedRows.map((row) => row.data);
        const selectedNotes = notes.filter((note) => selectedNoteIds.includes(note.id));
        const ownedNotes = selectedNotes.filter((note) => note.createdBy === operatorInformation?.id);

        switch (action?.name) {
            case CustomerNotesListActionNames.Edit: {
                if (ownedNotes.length === 1) {
                    updateNote(ownedNotes[0]);
                }
                break;
            }
            case CustomerNotesListActionNames.Delete: {
                if (ownedNotes.length < 1) {
                    return;
                }
                getRemoveConfirmation(ownedNotes.map((note) => note.id));
                break;
            }
            default:
                break;
        }
    };

    const dataGridRows = useMemo(() => notes?.map((note) => {
        const noteTags = note?.tagList ?? note?.tags ?? [];

        return ({
            key: note.id,
            data: note.id,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [CustomerNotesDataGridCellKeys.navigate]:
                    <NavigateCell noteTags={noteTags}/>,
                [CustomerNotesDataGridCellKeys.title]: note?.title,
                [CustomerNotesDataGridCellKeys.submittedByName]: note?.createdByName,
                [CustomerNotesDataGridCellKeys.source]:
                    <SourceCell noteTags={noteTags}/>,
                [CustomerNotesDataGridCellKeys.visibility]: note?.isPrivate ? t('visibility.private') : t('visibility.public'),
                [CustomerNotesDataGridCellKeys.submitDate]: note?.modifiedDateTime ?? note?.createdDateTime,
                [CustomerNotesDataGridCellKeys.content]:
                    <div
                        className={'note-content'}
                        dangerouslySetInnerHTML={{__html: note?.body ?? ''}}
                    />,
            },
        });
    }), [notes, t]);

    return (
        <PartnerDataGrid
            {...dataGridSaveConfig}
            initialLayoutState={{
                columns: notesListColumns,
            }}
            ref={dataGridControllerRef}
            columns={dataGridColumns}
            rows={dataGridRows}
            footerActions={footerActions}
            hideFooterActions={notes?.length < 1}
            onActionInvoked={onActionInvoked}
            hideSelectAll
            getExportInfo={getExportInfo}
            state={{
                pagination: {
                    currentPage: 1,
                    pageSize: 25,
                    length: notes?.length ?? 0,
                },
                loading: {state: loading},
                density: dataGridDensity,
            }}
        />
    );
};

export default CustomerNotesListView;
